import { useEffect, useReducer } from 'react';
import { Pim } from '@mummssoftware/utils';
import { AgendaRow, IDGMeeting, toIDGMeetingList } from './transforms';

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

type State =
  | { tag: 'idle' }
  | { tag: 'loading' }
  | { tag: 'success'; meetings: IDGMeeting[] }
  | { tag: 'error'; message: string }
  | { tag: 'skipped' };

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; meetings: IDGMeeting[] }
  | { type: 'FETCH_ERROR'; message: string }
  | { type: 'FETCH_SKIP' };

const reduce = (_state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { tag: 'loading' };
    case 'FETCH_SUCCESS':
      return { tag: 'success', meetings: action.meetings };
    case 'FETCH_ERROR':
      return { tag: 'error', message: action.message };
    case 'FETCH_SKIP':
      return { tag: 'skipped' };
    default:
      return _state;
  }
};

// ---------------------------------------------------------------------------
// Public shape
// ---------------------------------------------------------------------------

export type CrossPatientIDGData = {
  meetings: IDGMeeting[];
  loading: boolean;
  error: string | null;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetch submitted IDG meeting agenda rows for the given site from the pimreader
 * `idgmeetingagenda_vw` view (meetings with `stopdate` set), then shape them
 * into IDGMeeting objects grouped by meeting ID.
 *
 * @param pim      - Pim API client (must have pgRest initialised with pgRestUrl)
 * @param siteId   - numeric site ID as a string; required to scope the query
 * @param dateFrom - start of filter range (YYYY-MM-DD); meetings with agendastop on/after this date
 * @param dateTo   - end of filter range (YYYY-MM-DD); meetings with agendastart on/before this date
 */
export const useCrossPatientIDGData = (
  pim: Pim | undefined,
  siteId: string | undefined,
  dateFrom: string,
  dateTo: string,
): CrossPatientIDGData => {
  const [state, dispatch] = useReducer(reduce, { tag: 'idle' });

  useEffect(() => {
    if (!pim || !siteId) {
      dispatch({ type: 'FETCH_SKIP' });
      return undefined;
    }

    let cancelled = false;

    const fetch = async () => {
      dispatch({ type: 'FETCH_START' });

      const result = await pim.getIdgMeetingAgenda(siteId, dateFrom, dateTo);

      if (cancelled) return;

      if (result == null) {
        dispatch({ type: 'FETCH_SKIP' });
        return;
      }

      if (result instanceof Response || !Array.isArray(result)) {
        dispatch({
          type: 'FETCH_ERROR',
          message:
            result instanceof Response
              ? result.statusText || 'Failed to load IDG meetings'
              : 'Unexpected response from IDG meetings service',
        });
        return;
      }

      dispatch({
        type: 'FETCH_SUCCESS',
        meetings: toIDGMeetingList(result as AgendaRow[]),
      });
    };

    fetch();

    return () => {
      cancelled = true;
    };
  }, [pim, siteId, dateFrom, dateTo]);

  let error: string | null = null;
  if (state.tag === 'error') {
    error = state.message;
  } else if (state.tag === 'skipped') {
    error = 'IDG meeting data is not available (pimreader not configured).';
  }

  return {
    meetings: state.tag === 'success' ? state.meetings : [],
    loading: state.tag === 'loading' || state.tag === 'idle',
    error,
  };
};
